import { Motif, DesignTokens, MotifId } from '../types'

export const MOTIFS: Motif[] = [
  {
    id: 'electric',
    name: 'Electric',
    description: 'High-energy and futuristic with bold cyan accents',
    tokens: {
      primaryColor: 'cyan',
      secondaryColor: null,
      neutralColor: 'slate',
      typography: {
        displayFont: { family: 'Space Grotesk', weight: 'bold', size: '3xl' },
        bodyFont: { family: 'Inter', weight: 'normal', size: 'base' },
        monoFont: { family: 'JetBrains Mono' },
      },
      borderRadius: 'lg',
      shadow: 'lg',
    },
  },
  {
    id: 'fun',
    name: 'Fun',
    description: 'Playful and approachable with soft rounded elements',
    tokens: {
      primaryColor: 'pink',
      secondaryColor: 'violet',
      neutralColor: 'stone',
      typography: {
        displayFont: { family: 'Nunito', weight: 'bold', size: '3xl' },
        bodyFont: { family: 'Nunito', weight: 'normal', size: 'base' },
        monoFont: { family: 'Fira Code' },
      },
      borderRadius: 'xl',
      shadow: 'md',
    },
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Clean and trustworthy with classic blue tones',
    tokens: {
      primaryColor: 'blue',
      secondaryColor: null,
      neutralColor: 'gray',
      typography: {
        displayFont: { family: 'Inter', weight: 'semibold', size: '2xl' },
        bodyFont: { family: 'Inter', weight: 'normal', size: 'base' },
        monoFont: { family: 'Source Code Pro' },
      },
      borderRadius: 'md',
      shadow: 'sm',
    },
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Minimal and sleek with subtle sophistication',
    tokens: {
      primaryColor: 'zinc',
      secondaryColor: null,
      neutralColor: 'neutral',
      typography: {
        displayFont: { family: 'DM Sans', weight: 'medium', size: '2xl' },
        bodyFont: { family: 'DM Sans', weight: 'normal', size: 'sm' },
        monoFont: { family: 'IBM Plex Mono' },
      },
      borderRadius: 'sm',
      shadow: 'sm',
    },
  },
  {
    id: 'aggressive',
    name: 'Aggressive',
    description: 'Bold and direct with sharp edges and strong contrast',
    tokens: {
      primaryColor: 'red',
      secondaryColor: null,
      neutralColor: 'slate',
      typography: {
        displayFont: { family: 'Montserrat', weight: 'extrabold', size: '4xl' },
        bodyFont: { family: 'Work Sans', weight: 'normal', size: 'base' },
        monoFont: { family: 'JetBrains Mono' },
      },
      borderRadius: 'none',
      shadow: 'none',
    },
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Vibrant and confident with eye-catching violet',
    tokens: {
      primaryColor: 'violet',
      secondaryColor: 'fuchsia',
      neutralColor: 'zinc',
      typography: {
        displayFont: { family: 'Sora', weight: 'bold', size: '3xl' },
        bodyFont: { family: 'Inter', weight: 'normal', size: 'base' },
        monoFont: { family: 'Fira Code' },
      },
      borderRadius: 'lg',
      shadow: 'lg',
    },
  },
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Sophisticated and refined with classic serif typography',
    tokens: {
      primaryColor: 'amber',
      secondaryColor: null,
      neutralColor: 'stone',
      typography: {
        displayFont: { family: 'Playfair Display', weight: 'semibold', size: '3xl' },
        bodyFont: { family: 'Lora', weight: 'normal', size: 'base' },
        monoFont: { family: 'Source Code Pro' },
      },
      borderRadius: 'sm',
      shadow: 'sm',
    },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Ultra-clean with maximum breathing room',
    tokens: {
      primaryColor: 'gray',
      secondaryColor: null,
      neutralColor: 'gray',
      typography: {
        displayFont: { family: 'System UI', weight: 'medium', size: '2xl' },
        bodyFont: { family: 'System UI', weight: 'normal', size: 'sm' },
        monoFont: { family: 'Source Code Pro' },
      },
      borderRadius: 'md',
      shadow: 'none',
    },
  },
]

export const getMotifById = (id: string): Motif | undefined => {
  return MOTIFS.find((m) => m.id === id)
}

// Get a copy of a motif's tokens for use as a new design system's starting point
export const copyMotifTokens = (id: MotifId): DesignTokens => {
  const motif = getMotifById(id)
  if (!motif) {
    return MOTIFS[0].tokens
  }
  return JSON.parse(JSON.stringify(motif.tokens))
}

// Default tokens for a new system
export const DEFAULT_TOKENS: DesignTokens = copyMotifTokens('professional')
